import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { requireAdminRoute } from "@/lib/require-admin-route";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { apiFetch, ApiRequestError } from "@/lib/api-client";
import { humanizeApiError } from "@/lib/form-errors";
import { useToast } from "@/components/ui/Toast";
import { useTopbar } from "@/components/chrome/use-topbar";
import type { DynamoTermsInput } from "@/lib/settings-schemas";

const DEFAULTS: DynamoTermsInput = {
  maxLtvPctOfArv: 75,
  maxLtcPct: 100,
  ratePctBps: 975,
  points: 2,
  rehabBudgetRule: "Cover purchase + rehab + interest reserve",
  interestReserveMonths: 6,
  arvFactorBps: 13000,
};

const CANONICAL_AVM = 643000;

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export const Route = createFileRoute("/_app/settings/dynamo-terms")({
  beforeLoad: requireAdminRoute,
  component: DynamoTermsPage,
});

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function DynamoTermsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data, isLoading, error } = useQuery<DynamoTermsInput | null>({
    queryKey: ["settings", "dynamo-terms"],
    queryFn: () => apiFetch<DynamoTermsInput | null>("/api/settings/dynamo-terms"),
  });

  const [form, setForm] = useState<DynamoTermsInput>(DEFAULTS);
  const [seeded, setSeeded] = useState(false);

  const { setTopbar } = useTopbar();
  useEffect(() => {
    setTopbar({
      crumbs: [
        { label: "Operations" },
        { label: "Settings", to: "/settings" },
        { label: "Dynamo terms" },
      ],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar]);

  useEffect(() => {
    if (seeded) return;
    if (isLoading) return;
    setForm(data ?? DEFAULTS);
    setSeeded(true);
  }, [data, isLoading, seeded]);

  const save = useMutation({
    mutationFn: (payload: DynamoTermsInput) =>
      apiFetch<{ ok: true }>("/api/settings/dynamo-terms", {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      toast.success("Dynamo terms saved.");
      void qc.invalidateQueries({ queryKey: ["settings", "dynamo-terms"] });
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.status === 401) {
        void navigate({ to: `/login?redirect=${encodeURIComponent(pathname)}` });
        return;
      }
      toast.error(humanizeApiError(err).summary);
    },
  });

  const saveError = save.isError ? humanizeApiError(save.error) : null;
  const ltvAboveStandard = form.maxLtvPctOfArv > 75;

  const preview = useMemo(() => {
    const derivedArv = Math.round((CANONICAL_AVM * form.arvFactorBps) / 10000);
    const dynamoMax = Math.round((derivedArv * form.maxLtvPctOfArv) / 100);
    return { derivedArv, dynamoMax };
  }, [form.arvFactorBps, form.maxLtvPctOfArv]);

  if (isLoading) return <p>Loading…</p>;
  if (error) return <p className="text-red-400">Failed to load Dynamo terms.</p>;

  const rowClass =
    "flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4 rounded border border-rule-strong bg-surface px-4 py-3";
  const inputClass =
    "w-full md:w-28 rounded border border-rule-strong bg-surface px-3 py-1.5 text-right text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold";

  return (
    <div className="space-y-6">
      <div className="border-b border-rule pb-5">
        <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">Dynamo terms</h1>
        <p className="text-sm text-muted">
          Control the pricing and leverage rules used to re-underwrite competitor loans.{" "}
          <strong className="font-semibold text-ink">
            These fields drive the dollar story in every PDF.
          </strong>
        </p>
      </div>

      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(form);
        }}
      >
        <div className="rounded border border-rule-strong bg-surface px-4 py-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4">
            <div>
              <div className="text-sm font-medium">Max LTV (% of ARV)</div>
              <div className="text-xs text-muted">
                Maximum loan as a percentage of the property's after-repair value.
              </div>
            </div>
            <input
              type="number"
              min={0}
              max={100}
              value={form.maxLtvPctOfArv}
              onChange={(e) =>
                setForm((f) => ({ ...f, maxLtvPctOfArv: clamp(Number(e.target.value), 0, 100) }))
              }
              className={inputClass}
            />
          </div>
          {ltvAboveStandard ? (
            <span className="mt-2 block text-xs text-amber-300">
              Above the standard 75% — confirm this is intended.
            </span>
          ) : null}
        </div>

        <div className={rowClass}>
          <div>
            <div className="text-sm font-medium">Max LTC (%)</div>
            <div className="text-xs text-muted">
              Maximum loan as a percentage of total project cost.
            </div>
          </div>
          <input
            type="number"
            min={0}
            max={200}
            value={form.maxLtcPct}
            onChange={(e) =>
              setForm((f) => ({ ...f, maxLtcPct: clamp(Number(e.target.value), 0, 200) }))
            }
            className={inputClass}
          />
        </div>

        <div className={rowClass}>
          <div>
            <div className="text-sm font-medium">Annual rate (%)</div>
            <div className="text-xs text-muted">
              Stored as basis points × 100 (e.g. 9.75% = 975).
            </div>
          </div>
          <input
            type="number"
            min={0}
            max={100}
            step={0.01}
            value={form.ratePctBps / 100}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                ratePctBps: clamp(Math.round(Number(e.target.value) * 100), 0, 10000),
              }))
            }
            className={inputClass}
          />
        </div>

        <div className={rowClass}>
          <div>
            <div className="text-sm font-medium">Origination points</div>
            <div className="text-xs text-muted">Up-front points charged at close.</div>
          </div>
          <input
            type="number"
            min={0}
            max={10}
            value={form.points}
            onChange={(e) =>
              setForm((f) => ({ ...f, points: clamp(Number(e.target.value), 0, 10) }))
            }
            className={inputClass}
          />
        </div>

        <div className="rounded border border-rule-strong bg-surface px-4 py-3">
          <div className="text-sm font-medium">Rehab budget rule</div>
          <div className="text-xs text-muted">
            Free-form description of what the rehab budget must cover.
          </div>
          <textarea
            maxLength={500}
            rows={2}
            value={form.rehabBudgetRule}
            onChange={(e) => setForm((f) => ({ ...f, rehabBudgetRule: e.target.value }))}
            className="mt-2 w-full rounded border border-rule-strong bg-surface px-3 py-1.5 text-sm text-ink placeholder:text-muted-2 focus:outline-none focus:ring-1 focus:ring-gold"
          />
        </div>

        <div className={rowClass}>
          <div>
            <div className="text-sm font-medium">Interest reserve months</div>
            <div className="text-xs text-muted">Months of interest held back at funding.</div>
          </div>
          <input
            type="number"
            min={0}
            max={24}
            value={form.interestReserveMonths}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                interestReserveMonths: clamp(Number(e.target.value), 0, 24),
              }))
            }
            className={inputClass}
          />
        </div>

        <div className="rounded border border-rule-strong bg-surface px-4 py-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4">
            <div>
              <div className="text-sm font-medium">ARV factor (placeholder)</div>
              <div className="text-xs text-muted">
                Multiplier applied to AVM to derive ARV (0.80 – 2.00).
              </div>
            </div>
            <input
              type="number"
              min={0.8}
              max={2.0}
              step={0.01}
              value={form.arvFactorBps / 10000}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  arvFactorBps: clamp(Math.round(Number(e.target.value) * 10000), 8000, 20000),
                }))
              }
              className={inputClass}
            />
          </div>
          <span className="mt-2 block text-xs text-amber-300">
            Placeholder — pending real ARV derivation (see open issue).
          </span>
        </div>

        <div className="rounded border-l-2 border-tan-dark bg-paper-2 px-4 py-3">
          <div className="text-xs uppercase tracking-wide text-muted">Live preview</div>
          <p className="mt-1 text-sm text-muted">
            On a {usd.format(CANONICAL_AVM)} AVM, derived ARV ({usd.format(preview.derivedArv)})
            yields a Dynamo max of {usd.format(preview.dynamoMax)} at {form.maxLtvPctOfArv}% LTV.
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          {saveError ? <span className="text-xs text-neg">{saveError.summary}</span> : null}
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded bg-gold px-4 py-2 text-sm font-semibold text-ink disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
